import { useEffect, useState } from "react";
import { Star, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const Stars = ({ value, size = 14 }) => {
  const v = Math.round(value || 0);
  return (
    <div className="inline-flex gap-0.5 text-gold">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} fill={n <= v ? "currentColor" : "none"} />
      ))}
    </div>
  );
};

const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-2" data-testid="review-rating-picker">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid={`write-star-${n}`}
        aria-label={`${n} star${n === 1 ? "" : "s"}`}
        onClick={() => onChange(n)}
        className={`w-11 h-11 flex items-center justify-center border transition-all ${
          n <= value
            ? "border-gold text-gold bg-gold/10"
            : "border-white/15 text-white/30 hover:text-white"
        }`}
      >
        <Star size={20} fill={n <= value ? "currentColor" : "none"} />
      </button>
    ))}
  </div>
);

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

const WriteReviewForm = ({ gemstoneId, onSubmitted, onClose }) => {
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (body.trim().length < 10) {
      toast.error("Please write at least 10 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews/public-submit", {
        gemstone_id: gemstoneId,
        email: email.trim(),
        rating,
        title: title.trim() || undefined,
        body: body.trim(),
        display_name: displayName.trim() || undefined,
      });
      toast.success(
        data.verified
          ? "Thank you — your verified review is awaiting approval."
          : "Thank you — your review is awaiting moderation."
      );
      onSubmitted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit your review.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="write-review-form"
      className="relative bg-onyx border border-gold/40 p-7 mb-6"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 text-white/50 hover:text-white"
      >
        <X size={18} />
      </button>

      <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-2">
        Share your experience
      </div>
      <h3 className="font-heading text-2xl text-ivory mb-5">
        Write a review
      </h3>

      <div className="space-y-5">
        <div>
          <label className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold">
            Your rating
          </label>
          <div className="mt-2"><StarPicker value={rating} onChange={setRating} /></div>
        </div>

        <div>
          <label className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold">
            Email
          </label>
          <input
            data-testid="write-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="The email you used to purchase"
            className="mt-2 w-full bg-black border border-white/15 text-ivory px-3 py-2.5 text-sm focus:border-gold outline-none"
          />
          <div className="text-xs text-white/40 mt-1.5">
            We use this only to verify you bought this gem. Never shown publicly.
          </div>
        </div>

        <div>
          <label className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold">
            Title (optional)
          </label>
          <input
            data-testid="write-title"
            type="text"
            maxLength={160}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="A short headline"
            className="mt-2 w-full bg-black border border-white/15 text-ivory px-3 py-2.5 text-sm focus:border-gold outline-none"
          />
        </div>

        <div>
          <label className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold">
            Your review
          </label>
          <textarea
            data-testid="write-body"
            required
            rows={5}
            maxLength={2000}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="A few sentences about your gemstone and your experience…"
            className="mt-2 w-full bg-black border border-white/15 text-ivory px-3 py-2.5 text-sm focus:border-gold outline-none resize-y"
          />
          <div className="text-xs text-white/40 mt-1.5">{body.length} / 2000</div>
        </div>

        <div>
          <label className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold">
            Display name (optional)
          </label>
          <input
            data-testid="write-display-name"
            type="text"
            maxLength={80}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="James L."
            className="mt-2 w-full bg-black border border-white/15 text-ivory px-3 py-2.5 text-sm focus:border-gold outline-none"
          />
        </div>

        <button
          type="submit"
          data-testid="write-submit"
          disabled={submitting}
          className="w-full bg-gold text-black font-accent uppercase tracking-[0.24em] text-[10px] py-3.5 hover:bg-ivory transition-colors disabled:opacity-50"
        >
          {submitting ? "Submitting…" : "Submit review"}
        </button>

        <p className="text-xs text-white/40 italic text-center">
          All reviews are moderated by a senior advisor before going live.
        </p>
      </div>
    </form>
  );
};

const ReviewsSection = ({ gemstoneId }) => {
  const [stats, setStats] = useState({ count: 0, average: null });
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [writing, setWriting] = useState(false);

  const load = () => {
    if (!gemstoneId) return;
    Promise.all([
      api.get(`/gemstones/${gemstoneId}/review-stats`).then((r) => r.data).catch(() => ({ count: 0, average: null })),
      api.get(`/gemstones/${gemstoneId}/reviews`).then((r) => r.data).catch(() => ({ reviews: [] })),
    ]).then(([s, list]) => {
      setStats(s || { count: 0, average: null });
      setReviews(list?.reviews || []);
      setLoading(false);
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gemstoneId]);

  if (loading) return null;

  return (
    <section data-testid="reviews-section" className="mt-16 border-t border-white/10 pt-12">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-8">
        <h2 className="font-heading text-3xl text-ivory">Custodian reviews</h2>
        <div className="flex items-center gap-5 flex-wrap">
          {stats.count > 0 && (
            <div className="flex items-center gap-3">
              <Stars value={stats.average} size={16} />
              <span className="font-heading text-2xl text-gold">
                {stats.average?.toFixed(1)}
              </span>
              <span className="text-white/40 text-sm">
                ({stats.count} review{stats.count === 1 ? "" : "s"})
              </span>
            </div>
          )}
          {!writing && (
            <button
              type="button"
              data-testid="write-review-btn"
              onClick={() => setWriting(true)}
              className="inline-flex items-center gap-2 font-accent text-[10px] tracking-[0.28em] uppercase border border-gold text-gold px-4 py-2.5 hover:bg-gold hover:text-black transition-colors"
            >
              <Pencil size={12} /> Write a review
            </button>
          )}
        </div>
      </div>

      {writing && (
        <WriteReviewForm
          gemstoneId={gemstoneId}
          onClose={() => setWriting(false)}
          onSubmitted={() => {
            setWriting(false);
            load();
          }}
        />
      )}

      {stats.count === 0 && !writing && (
        <div className="bg-onyx border border-white/10 p-10 text-center text-white/40">
          Be the first to share your experience with this gemstone.
        </div>
      )}

      <div className="space-y-5">
        {reviews.map((r) => (
          <article
            key={r.id}
            data-testid={`review-${r.id}`}
            className="bg-onyx border border-white/10 p-6"
          >
            <header className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div>
                <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold">
                  {r.display_name || "Custodian"}
                </div>
                <div className="text-xs text-white/40 mt-1">
                  {formatDate(r.created_at)}
                  {r.verified && (
                    <>
                      {" "}
                      <span className="text-white/30">·</span>{" "}
                      <span className="text-emerald-300">Verified purchase</span>
                    </>
                  )}
                </div>
              </div>
              <Stars value={r.rating} />
            </header>
            {r.title && (
              <h3 className="font-heading text-lg text-ivory mb-1.5">{r.title}</h3>
            )}
            <p className="text-white/80 text-sm leading-relaxed whitespace-pre-line">
              {r.body}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
};

export default ReviewsSection;
